import * as Tooltip from "@radix-ui/react-tooltip";
import { formatBackupDate } from "../../util/backup.date";
import { useEffect, useState } from "react";
import styled from "styled-components";
import {
  fetchBackupContentsAsOfBackupTime,
  fetchBackupUserListLoad,
} from "../../api/admin";
import { fetchLoadClientLogin } from "../../api/auth";
import {
  BackupFileListContainer,
  DeviceDeleteTooltip,
} from "../../style/contents/backup/backup";
import type { BackupFileItem } from "../../view/contents/backup/backup.file.list";
import type { BackupUser } from "../../view/contents/backup/backup.user";
import type { BackupDateContext } from "./backup.date.list";
import ModalCenterLayout from "../modal/modal.center.layout";
import SearchForm from "../common/search.form";
import FileList from "./file.list";

interface Props {
  context: BackupDateContext;
  backupDate: string;
  role: string;
  onClose: () => void;
}

const BackupDetailsModal = ({ context, backupDate, role, onClose }: Props) => {
  const [pathHistory, setPathHistory] = useState([context.rootDirectoryPath]);
  const currentPath = pathHistory[pathHistory.length - 1];
  const openFolder = (path: string) => {
    setPathHistory((previous) =>
      previous[previous.length - 1] === path ? previous : [...previous, path],
    );
  };
  const goBack = () => {
    setPathHistory((previous) =>
      previous.length > 1 ? previous.slice(0, -1) : previous,
    );
  };
  const [files, setFiles] = useState<BackupFileItem[]>([]);
  const [keyword, setKeyword] = useState("");
  const [pageSize, setPageSize] = useState("10");
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [loginToken, setLoginToken] = useState("");
  const [tokenError, setTokenError] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    fetchBackupContentsAsOfBackupTime(String(context.userId), {
      deviceId: context.deviceId,
      directoryPath: currentPath,
      backupDate,
      isAdmin: role === "admin",
    })
      .then((response) => {
        if (active) setFiles(response.filePathlist || []);
      })
      .catch(() => {
        if (active) {
          setFiles([]);
          setLoadError(true);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [context.userId, context.deviceId, currentPath, backupDate, role, retry]);

  useEffect(() => {
    if (role !== "admin") return;
    let active = true;
    setTokenError(false);
    const login = async () => {
      const companyCode = localStorage.getItem("companyCode") || "";
      const response = await fetchBackupUserListLoad(
        Number(localStorage.getItem("userId")),
        companyCode,
      );
      const user = (response.userList as BackupUser[]).find(
        (item) => item.userId === context.userId,
      );
      if (!user) throw new Error("사용자 정보가 없습니다.");
      const result = await fetchLoadClientLogin({
        companyCode,
        userAccount: user.userAccount,
        password: user.password,
        deviceId: "",
      });
      if (!result.jwtToken) throw new Error("토큰 정보가 없습니다.");
      if (active) setLoginToken(result.jwtToken);
    };
    login().catch(() => {
      if (active) setTokenError(true);
    });
    return () => {
      active = false;
    };
  }, [context.userId, role, retry]);

  const filteredFiles = files.filter((file) => {
    const search = keyword.toLowerCase();
    return [
      file.fileName,
      file.createDate,
      file.path,
      String(file.size),
      file.fileType === "d" ? "폴더" : "파일",
    ].some((value) => (value || "").toLowerCase().includes(search));
  });

  return (
    <ModalCenterLayout isVisible closedHandler={onClose} size="m">
      <Details
        role="dialog"
        aria-modal="true"
        aria-labelledby="backup-details-title"
      >
        <div className="backup-modal-header">
          <h3 id="backup-details-title">백업 세부 정보 보기</h3>
          <button
            type="button"
            aria-label="백업 세부 정보 닫기"
            onClick={onClose}
          >
            ×
          </button>
        </div>
        <BackupFileListContainer>
          <div className="file-list-container">
            <div className="top">
              <div className="left">
                <Tooltip.Provider delayDuration={200}>
                  <Tooltip.Root>
                    <Tooltip.Trigger asChild>
                      <span
                        className="folder-back-tooltip"
                        tabIndex={pathHistory.length === 1 ? 0 : undefined}
                        aria-label={
                          pathHistory.length === 1 ? "뒤로가기" : undefined
                        }
                      >
                        <button
                          type="button"
                          className="folder-back-button"
                          aria-label="이전 폴더로 돌아가기"
                          disabled={pathHistory.length === 1}
                          onClick={goBack}
                        >
                          <img src="/assets/left-arrow.png" alt="" />
                        </button>
                      </span>
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <DeviceDeleteTooltip
                        side="top"
                        sideOffset={6}
                        collisionPadding={8}
                      >
                        뒤로가기
                        <Tooltip.Arrow fill="#333333" />
                      </DeviceDeleteTooltip>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                </Tooltip.Provider>
                <span>{formatBackupDate(backupDate)}</span>
              </div>
              <div className="right">
                <SearchForm
                  onSearch={setKeyword}
                  onPageSizeChange={setPageSize}
                  pageSizeHidden
                />
              </div>
            </div>
            {tokenError && (
              <div role="alert">
                일부 작업에 필요한 인증 정보를 불러오지 못했습니다.{" "}
                <button
                  type="button"
                  onClick={() => setRetry((value) => value + 1)}
                >
                  다시 시도
                </button>
              </div>
            )}
            {loadError ? (
              <div role="alert">
                백업 파일 목록을 불러오지 못했습니다.{" "}
                <button
                  type="button"
                  onClick={() => setRetry((value) => value + 1)}
                >
                  다시 시도
                </button>
              </div>
            ) : (
              <FileList
                key={`${currentPath}-${keyword}`}
                data={filteredFiles}
                selectedBackupDate={backupDate}
                pageSize={Number(pageSize)}
                paginationEnabled={false}
                onFolderClick={openFolder}
                id={context.userId}
                deviceId={context.deviceId}
                isLoading={loading}
                role={role}
                loginToken={loginToken}
              />
            )}
          </div>
        </BackupFileListContainer>
      </Details>
    </ModalCenterLayout>
  );
};

const Details = styled.div`
  max-height: 85vh;
  overflow: auto;
  background: white;

  .file-list-container {
    height: auto;
    padding: 2rem;
    .top {
      margin: 0 !important;
      padding: 0 0 20px !important;
      .left {
        display: flex;
        align-items: center;
        gap: 10px;
        .folder-back-tooltip {
          display: inline-flex;
        }
        .folder-back-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          border: 1px solid #dce5e3;
          border-radius: 4px;
          background: white;
          color: #333333;
          font-size: 20px;
          img {
            height: 100%;
          }
          cursor: pointer;
          &:disabled {
            opacity: 0.35;
            cursor: default;
          }
          &:enabled:hover {
            background: #f7fbfa;
          }
        }
        font-size: 14px;
      }
    }
  }
`;

export default BackupDetailsModal;
